import { FormFieldType } from '../../../../../../common/types/form/form-fields';
import { AttachmentField } from './attachment-field';
import { CascadingSelectInput } from './cascading-select-input';
import { CheckboxInput } from './checkbox-input';
import { DateInput } from './date-input';
import { DateTimeInput } from './date-time-input';
import { EmailInput } from './email-input';
import { FieldContainer } from './field-container';
import { FieldHelperMessage } from './field-helper-message';
import { FieldLabel } from './field-label';
import { LocalTimeInput } from './local-time-input';
import { MultipleSelectInput } from './multiple-select-input';
import { NumberInput } from './number-input';
import { PlainTextParagraphInput } from './paragraph-plain-text-input';
import { RichTextParagraphInput } from './paragraph-rich-text-input';
import { RadioInput } from './radio-input';
import { RatingInput } from './rating-input';
import { ScaleInput } from './scale-input';
import { SingleSelectInput } from './single-select-input';
import { TextLongInput } from './text-long-input';
import { TextShortInput } from './text-short-input';
import { UrlInput } from './url-input';

/**
 * Don't explicitly type `defaultFormFieldComponentRenderers` to FormFieldComponentRenderers.
 *
 * If we cast the type, then all the properties become optional and require the consumer to check for
 * optionality. We want to make it easy to consume the default renderers so want to type this object to
 * have no optional properties.
 */
export const defaultFormFieldComponentRenderers: {
	readonly Container: import('../../../../../..').FormFieldComponentRenderer;
	readonly HelperMessage: import('../../../../../..').FormFieldComponentRenderer;
	readonly Label: ({
		afterLabelIcon,
		fieldId,
		fieldInputId,
	}: import('./field-label/types').LabelRendererProps) => React.JSX.Element;
	readonly Input: {
		readonly attachment: ({
			fieldId,
			fieldInputId,
			fieldErrorId,
		}: import('../../../../../..').FormFieldComponentRendererProps) => React.JSX.Element;
		readonly cascadingSelect: ({
			fieldId,
			fieldInputId,
			fieldErrorId,
		}: import('../../../../../..').FormFieldComponentRendererProps) => React.JSX.Element;
		readonly checkbox: ({
			fieldId,
			fieldInputId,
			fieldErrorId,
		}: import('../../../../../..').FormFieldComponentRendererProps) => React.JSX.Element;
		readonly date: ({
			fieldId,
			fieldInputId,
			fieldErrorId,
		}: import('../../../../../..').FormFieldComponentRendererProps) => React.JSX.Element;
		readonly dateTime: ({
			fieldId,
			fieldInputId,
			fieldErrorId,
		}: import('../../../../../..').FormFieldComponentRendererProps) => React.JSX.Element;
		readonly email: ({
			fieldId,
			fieldInputId,
			fieldErrorId,
		}: import('../../../../../..').FormFieldComponentRendererProps) => React.JSX.Element;
		readonly localTime: ({
			fieldId,
			fieldInputId,
			fieldErrorId,
		}: import('../../../../../..').FormFieldComponentRendererProps) => React.JSX.Element;
		readonly longText: ({
			fieldId,
			fieldInputId,
			fieldErrorId,
		}: import('../../../../../..').FormFieldComponentRendererProps) => React.JSX.Element;
		readonly multipleSelect: ({
			fieldId,
			fieldInputId,
			fieldErrorId,
		}: import('../../../../../..').FormFieldComponentRendererProps) => React.JSX.Element;
		readonly number: ({
			fieldId,
			fieldInputId,
			fieldErrorId,
		}: import('../../../../../..').FormFieldComponentRendererProps) => React.JSX.Element;
		readonly paragraph: ({
			fieldId,
			fieldInputId,
			fieldErrorId,
		}: import('../../../../../..').FormFieldComponentRendererProps) => React.JSX.Element;
		readonly radio: ({
			fieldId,
			fieldInputId,
			fieldErrorId,
		}: import('../../../../../..').FormFieldComponentRendererProps) => React.JSX.Element;
		readonly rating: ({
			fieldId,
			fieldInputId,
			fieldErrorId,
		}: import('../../../../../..').FormFieldComponentRendererProps) => React.JSX.Element;
		readonly scale: ({
			fieldId,
			fieldInputId,
			fieldErrorId,
		}: import('../../../../../..').FormFieldComponentRendererProps) => React.JSX.Element;
		readonly richTextParagraph: ({
			fieldId,
			fieldInputId,
			fieldErrorId,
		}: import('../../../../../..').FormFieldComponentRendererProps) => React.JSX.Element;
		readonly shortText: ({
			fieldId,
			fieldInputId,
			fieldErrorId,
		}: import('../../../../../..').FormFieldComponentRendererProps) => React.JSX.Element;
		readonly singleSelect: ({
			fieldId,
			fieldInputId,
			fieldErrorId,
		}: import('../../../../../..').FormFieldComponentRendererProps) => React.JSX.Element;
		readonly url: ({
			fieldId,
			fieldInputId,
			fieldErrorId,
		}: import('../../../../../..').FormFieldComponentRendererProps) => React.JSX.Element;
	};
} = {
	Container: FieldContainer,
	HelperMessage: FieldHelperMessage,
	Label: FieldLabel,
	Input: {
		[FormFieldType.ATTACHMENT]: AttachmentField,
		[FormFieldType.CASCADING_SELECT]: CascadingSelectInput,
		[FormFieldType.CHECKBOX]: CheckboxInput,
		[FormFieldType.DATE]: DateInput,
		[FormFieldType.DATE_TIME]: DateTimeInput,
		[FormFieldType.EMAIL]: EmailInput,
		[FormFieldType.LOCAL_TIME]: LocalTimeInput,
		[FormFieldType.LONG_TEXT]: TextLongInput,
		[FormFieldType.MULTIPLE_SELECT]: MultipleSelectInput,
		[FormFieldType.NUMBER]: NumberInput,
		[FormFieldType.PARAGRAPH]: PlainTextParagraphInput,
		[FormFieldType.RADIO]: RadioInput,
		[FormFieldType.RATING]: RatingInput,
		[FormFieldType.SCALE]: ScaleInput,
		[FormFieldType.RICH_TEXT_PARAGRAPH]: RichTextParagraphInput,
		[FormFieldType.SHORT_TEXT]: TextShortInput,
		[FormFieldType.SINGLE_SELECT]: SingleSelectInput,
		[FormFieldType.URL]: UrlInput,
	},
} as const;
